import React from 'react'
import * as filestack from 'filestack-js'
import {log} from '@/logger'
import {Box} from '../../../utils/chakraMonomorphic'
import './filestackOverrides.css'

import {FILESTACK_MP_API_KEY} from '../../../constants'

const FS_FILE_RETRY = 2
const FS_FILE_RETRY_TIMEOUT = 60000
const FS_FILE_INTELLIGENT_CHUCK_SIZE = 1 * 1024 * 1024 // 1 MB
const FS_MAX_FILES_COUNT = 1
const FS_MAX_FILE_SIZE = 1024 * 1024 * 20 // 20971520 === 20MB

const defaultOptions: filestack.PickerOptions = {
  displayMode: filestack.PickerDisplayMode.dropPane,
  fromSources: ['local_file_system'],
  container: 'divDropPane',
  accept: ['image/jpeg', 'image/png'],
  uploadConfig: {
    retry: FS_FILE_RETRY,
    timeout: FS_FILE_RETRY_TIMEOUT,
    intelligent: true,
    intelligentChunkSize: FS_FILE_INTELLIGENT_CHUCK_SIZE,
  },
  customText: {
    'Drag and Drop, Copy and Paste Files': 'Upload Photo',
  },
  allowManualRetry: true, // Prevent modal close on upload failure and allow users to retry.
  disableTransformer: true,
  maxFiles: FS_MAX_FILES_COUNT,
  maxSize: FS_MAX_FILE_SIZE,
  uploadInBackground: false,
}

interface Props {
  onFileUploadFinished: NonNullable<
    filestack.PickerOptions['onFileUploadFinished']
  >
  onFileUploadFailed: NonNullable<filestack.PickerOptions['onFileUploadFailed']>
  onFileUploadStarted: NonNullable<
    filestack.PickerOptions['onFileUploadStarted']
  >
}

export const SimplePhotoUploader: React.FC<React.PropsWithChildren<Props>> =
  React.memo(
    ({onFileUploadFinished, onFileUploadFailed, onFileUploadStarted}) => {
      const pickerOptions = React.useMemo(
        () => ({
          ...defaultOptions,
          onFileUploadFinished,
          onFileUploadFailed,
          onFileUploadStarted,
        }),
        [onFileUploadFinished, onFileUploadFailed, onFileUploadStarted],
      )

      const fileStackClient = React.useRef<filestack.Client>(
        filestack.init(FILESTACK_MP_API_KEY),
      )

      // Tracks whether the component is still mounted, so the async picker-open
      // failure handler below can tell an expected unmount-time rejection (the
      // modal closed mid-init, so #divDropPane left the document) from a real
      // one. Mirrors the Albums PhotoSelector fix (AB#21200 / AB#21556).
      const isMountedRef = React.useRef(true)
      // The `= true` is NOT redundant with the useRef(true) default: under
      // StrictMode the mount→cleanup→mount cycle runs the cleanup (→ false) and
      // then this body again, so the body must restore true on every (re)mount.
      // Caveat: in StrictMode's dev-only mount→cleanup→remount cycle the remount
      // synchronously restores isMountedRef.current = true before the open()
      // rejection microtask runs, so a first-mount rejection is misclassified the
      // other way — as a real error (spurious log.error), not an after-unmount
      // breadcrumb. Production single-mount is unaffected.
      React.useEffect(() => {
        isMountedRef.current = true
        return (): void => {
          isMountedRef.current = false
        }
      }, [])

      React.useEffect(() => {
        const picker = fileStackClient.current.picker(pickerOptions)

        // open() binds the #divDropPane container inside an awaited promise, so
        // its rejection escapes a synchronous try/catch. When the container has
        // left the document before init completes the SDK throws "Filestack
        // Picker Initialize: Container ... not found in document". Handle the
        // async rejection via .catch and classify on isMountedRef (see AB#21556,
        // mirroring the Albums fix AB#21200): an after-unmount rejection is
        // expected (breadcrumb), anything else is a real error. The retained
        // try/catch covers a synchronous throw from open() before any promise.
        // NOTE: this open()-with-mount-guard block is also in PhotoSelector.tsx;
        // if a third Filestack call site is added, extract to a shared hook
        // (e.g. useFilestackPickerOpen()) and pull the breadcrumb string into a
        // named constant at that point.
        try {
          Promise.resolve(picker.open()).catch((e: unknown) => {
            if (!isMountedRef.current) {
              log.breadcrumb(
                'Filestack picker open() rejected after unmount; ignoring',
              )
              return
            }
            if (e instanceof Error) {
              log.error(e)
            } else {
              log.error(new Error(JSON.stringify(e)))
            }
          })
        } catch (e) {
          if (e instanceof Error) {
            log.error(e)
          } else {
            log.error(new Error(JSON.stringify(e)))
          }
        }

        return (): void => {
          // close() awaits the SAME internal init promise open() does, so on an
          // unmount mid-init it rejects asynchronously with the same
          // "#divDropPane not found" error. A synchronous try/catch cannot catch
          // a promise rejection (the trap called out on open() above), so the
          // teardown rejection escaped as an unhandled error (AB#21629). Handle it via
          // .catch; a teardown-time rejection is the expected unmount-mid-init
          // case, so record a breadcrumb rather than counting it as an error.
          // A synchronous throw before any promise is treated the same way.
          // Mirrors PhotoSelector.tsx.
          const ignoreTeardownRejection = (e: unknown): void => {
            log.breadcrumb(
              'Filestack picker close() rejected during teardown; ignoring',
              {error: e instanceof Error ? e.message : String(e)},
            )
          }
          try {
            Promise.resolve(picker.close()).catch(ignoreTeardownRejection)
          } catch (e) {
            ignoreTeardownRejection(e)
          }
        }
      }, [pickerOptions])

      return <Box data-testid='divDropPane' id='divDropPane' />
    },
  )

SimplePhotoUploader.displayName = 'SimplePhotoUploader'
